import { Loader2, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchScanStatus, fetchSectorLeadership, fetchThemes, triggerRefresh } from '../api';
import { SectorItem, ThemeItem } from '../types';
import GuideCard from './GuideCard';

interface Props {
  onSelectTicker: (ticker: string, name: string) => void;
}

// 🎨 규격 V2(08-23) 공용 부품 — 버튼은 고스트/켜짐 두 가지뿐, 색은 dc 토큰만(밝은 원색·알약 탭 금지).
const BTN = 'inline-flex items-center gap-1.5 h-[26px] px-2 sm:h-[29px] sm:px-[11px] rounded-lg border text-[11.5px] sm:text-xs transition-colors';
const GHOST = `${BTN} border-[#202A3C] text-dc-ink2/90 font-medium hover:border-[#33405A] hover:text-dc-ink1 disabled:opacity-40 disabled:hover:border-[#202A3C]`;
const ON = `${BTN} border-dc-point/35 bg-dc-point/10 text-dc-point font-semibold`;
const MINICHIP = 'inline-flex items-center gap-0.5 h-[18px] px-1.5 rounded-[5px] border text-[10.5px] font-medium whitespace-nowrap';
// 종목 칩(누르면 상세) — 고스트 버튼의 작은 판
const TICKCHIP = 'inline-flex items-center gap-1 h-[22px] px-2 rounded-md border border-dc-line1 bg-dc-bg1 text-[11.5px] text-dc-ink2 hover:border-[#33405A] hover:text-dc-ink1 transition-colors';

// 추세 배지 — 강함=웜(상승색) / 약함=쿨(하락색) / 중립=중립칩.
//   초록(dc.ok)은 "확실히 좋음"(조건 통과·양호 판정)에만 쓰는 색이라 여기선 안 쓴다(규격 V2).
const TREND_STYLE: Record<string, string> = {
  '강함': 'border-dc-up/40 bg-dc-up/[0.08] text-dc-up',
  '중립': 'border-dc-line1 bg-dc-bg1 text-dc-ink2',
  '약함': 'border-dc-down/35 bg-dc-down/[0.07] text-dc-down',
};

// 상대강도 막대 — 채움은 앰버(달성) / 회색(미달) 두 가지뿐. 초록·노랑 3단 채움은 08-23 은퇴.
//   앰버 기준은 전 화면 공통 90+(등급 '최상급'·전체 스캔 상대 힘과 같은 문턱).
function rsColor(v: number): string {
  return v >= 90 ? 'bg-dc-point' : 'bg-dc-ink3';
}

export default function ThemePanel({ onSelectTicker }: Props) {
  const [themes, setThemes] = useState<ThemeItem[]>([]);
  const [sectors, setSectors] = useState<SectorItem[]>([]);
  const [view, setView] = useState<'theme' | 'sector'>('theme');
  const [loading, setLoading] = useState(true);
  const [scanTime, setScanTime] = useState<string | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchThemes();
      setThemes(r.themes);
      setScanTime(r.scan_time);
      fetchSectorLeadership().then((s) => setSectors(s.sectors)).catch(() => setSectors([]));
      if (r.themes.length === 0) {
        // 스캔 미완료일 수 있음 → 폴링
        const s = await fetchScanStatus();
        if (s.scanning || s.cached_count === 0) {
          stopPoll();
          pollRef.current = setInterval(async () => {
            const rr = await fetchThemes();
            if (rr.themes.length > 0) {
              stopPoll();
              setThemes(rr.themes);
              setScanTime(rr.scan_time);
            }
          }, 3000);
        }
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  // 새로고침 = 실제 재스캔 트리거 → 완료까지 대기 → 데이터 갱신 (캐시만 다시 읽던 버그 수정)
  const refresh = useCallback(async () => {
    setLoading(true);
    stopPoll();
    try {
      await triggerRefresh(false);
      // 스캔 완료까지 폴링 (최대 ~90초)
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const s = await fetchScanStatus();
        if (!s.scanning) break;
      }
      const r = await fetchThemes();
      setThemes(r.themes);
      setScanTime(r.scan_time);
      fetchSectorLeadership().then((s) => setSectors(s.sectors)).catch(() => setSectors([]));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return stopPoll;
  }, [load]);

  const maxRs = Math.max(100, ...themes.map((t) => t.avg_rs));

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-6 max-w-3xl mx-auto w-full">
      <div className="-mx-4 -mt-2 mb-2">
        <GuideCard id="themes" title="주도 테마 사용법">
          <p className="mb-1.5"><b className="text-dc-ink1">이 화면은?</b> 지금 시장을 이끄는 <b>주도 테마(섹터)</b> 순위예요 — 돈이 어디로 몰리는지 보입니다.</p>
          <p><b className="text-dc-ink2">이렇게 쓰세요:</b> ① RS 높은 상위 테마 = 주도 섹터 → ② 그 테마 종목에 집중(주도주가 더 잘 오름) → ③ 종목 클릭 → 상세. 시장이 약하면 주도 테마도 흔들리니 상단 시장국면과 같이 보세요.</p>
        </GuideCard>
      </div>
      <div className="flex items-center justify-between mb-2 gap-2">
        {/* 토글: ETF 테마 / 개별주 업종 */}
        <div className="flex gap-1.5">
          {([['theme', 'ETF 테마'], ['sector', '개별주 업종']] as const).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={view === v ? ON : GHOST}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className={GHOST}
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> {loading ? '분석 중...' : '새로고침'}
        </button>
      </div>
      <p className="text-dc-ink3 text-[11.5px] mb-5">
        {view === 'theme'
          ? '테마 ETF의 상대강도(RS)를 묶어 지금 시장이 어디 중심으로 도는지 보여줍니다.'
          : '개별 종목을 네이버 업종(섹터)으로 묶어 평균 RS로 정렬 — 어느 업종의 개별주가 강한지. 업종 분류는 네이버 참고치.'}
        {scanTime && <span className="ml-1 text-dc-ink3/70">· {new Date(scanTime).toLocaleString('ko-KR')}</span>}
      </p>

      {/* ── ETF 테마 ── */}
      {view === 'theme' && (loading && themes.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-20 text-dc-ink3 text-[13px]">
          <Loader2 size={16} className="animate-spin" /> 불러오는 중...
        </div>
      ) : themes.length === 0 ? (
        <div className="text-center py-20 text-dc-ink3 text-[13px]">
          전체 스캔이 완료되면 주도 테마가 표시됩니다.
        </div>
      ) : (
        <div className="border-t border-dc-hair">
          {themes.map((t, idx) => (
            <div key={t.theme} className="border-b border-dc-hair/80 py-3">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-dc-ink3 tabular-nums text-[11.5px] w-5 text-right">{idx + 1}</span>
                <span className="text-dc-ink1 text-[14px] font-[750]">{t.theme}</span>
                <span className={`${MINICHIP} ${TREND_STYLE[t.trend] ?? TREND_STYLE['중립']}`}>
                  {t.trend === '강함' ? <TrendingUp size={10} /> : t.trend === '약함' ? <TrendingDown size={10} /> : null}
                  {t.trend}
                </span>
                <span className="text-dc-ink3 text-[11.5px]">ETF {t.count}</span>
                <span className="ml-auto tabular-nums text-[14px] font-[750] text-dc-ink1">RS {t.avg_rs}</span>
              </div>
              <div className="h-1 bg-dc-hair rounded-full overflow-hidden mb-2.5">
                <div className={`h-full rounded-full ${rsColor(t.avg_rs)}`} style={{ width: `${(t.avg_rs / maxRs) * 100}%` }} />
              </div>
              <div className="flex flex-wrap gap-1.5 pl-8">
                {t.top.map((m) => (
                  <button
                    key={m.ticker}
                    onClick={() => onSelectTicker(m.ticker, m.name)}
                    className={TICKCHIP}
                  >
                    <span className="truncate max-w-[180px]">{m.name}</span>
                    <span className="tabular-nums text-dc-ink1">{m.rs_rank}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* ── 개별주 업종 ── */}
      {view === 'sector' && (sectors.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-20 text-dc-ink3 text-[13px]">
          {loading ? <><Loader2 size={16} className="animate-spin" /> 불러오는 중...</> : '업종 데이터를 불러오는 중이거나 없습니다 (첫 수집은 ~30초).'}
        </div>
      ) : (
        <div className="border-t border-dc-hair">
            {sectors.slice(0, 15).map((s, idx) => {
              const sMax = Math.max(100, ...sectors.map((x) => x.avg_rs));
              return (
                <div key={s.sector} className="border-b border-dc-hair/80 py-3">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-dc-ink3 tabular-nums text-[11.5px] w-5 text-right">{idx + 1}</span>
                    <span className="text-dc-ink1 text-[14px] font-[750]">{s.sector}</span>
                    <span className={`${MINICHIP} ${TREND_STYLE[s.trend] ?? TREND_STYLE['중립']}`}>
                      {s.trend === '강함' ? <TrendingUp size={10} /> : s.trend === '약함' ? <TrendingDown size={10} /> : null}
                      {s.trend}
                    </span>
                    <span className="text-dc-ink3 text-[11.5px]">종목 {s.count}</span>
                    <span className="ml-auto tabular-nums text-[14px] font-[750] text-dc-ink1">RS {s.avg_rs}</span>
                  </div>
                  <div className="h-1 bg-dc-hair rounded-full overflow-hidden mb-2.5">
                    <div className={`h-full rounded-full ${rsColor(s.avg_rs)}`} style={{ width: `${(s.avg_rs / sMax) * 100}%` }} />
                  </div>
                  <div className="flex flex-wrap gap-1.5 pl-8">
                    {s.top.map((m) => (
                      <button
                        key={m.ticker}
                        onClick={() => onSelectTicker(m.ticker, m.name)}
                        className={TICKCHIP}
                      >
                        <span className="truncate max-w-[180px]">{m.name}</span>
                        <span className="tabular-nums text-dc-ink1">{m.rs_rank}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
      ))}
      </div>
    </div>
  );
}
